/**
 * NAT-2497 — where the selection lives, and how the transport signals a refusal.
 *
 * Per-tab (`sessionStorage`), not the shared `localStorage` key the agent
 * picker uses: a shared key means a switch in one tab moves every other tab's
 * next write to the new venue (spec §6) — the cross-tab race NAT-2494 closed
 * at confirm time. `localStorage` holds a STARTUP PREFERENCE only — a new tab
 * seeds itself from it and then owns its own selection.
 */

export const ACTING_CLIENT_TAB_KEY = 'actingClientId';
export const ACTING_CLIENT_PREFERENCE_KEY = 'actingClientPreference';
/** Dispatched on `window` by the transport when the API refuses with a 403. */
export const ACTING_CLIENT_REFUSED_EVENT = 'acting-client:refused';

const safe = <T>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

export const readTabSelection = (): string | null =>
  safe(() => sessionStorage.getItem(ACTING_CLIENT_TAB_KEY), null) || null;

export const writeTabSelection = (id: string | null): void => {
  safe(() => {
    if (id) sessionStorage.setItem(ACTING_CLIENT_TAB_KEY, id);
    else sessionStorage.removeItem(ACTING_CLIENT_TAB_KEY);
    return undefined;
  }, undefined);
};

export const readPreference = (): string | null =>
  safe(() => localStorage.getItem(ACTING_CLIENT_PREFERENCE_KEY), null) || null;

export const writePreference = (id: string): void => {
  safe(() => localStorage.setItem(ACTING_CLIENT_PREFERENCE_KEY, id), undefined);
};

/** On sign-out: the next person on this browser is asked, not seated in the last venue. */
export const clearActingClientStorage = (): void => {
  safe(() => {
    sessionStorage.removeItem(ACTING_CLIENT_TAB_KEY);
    localStorage.removeItem(ACTING_CLIENT_PREFERENCE_KEY);
    return undefined;
  }, undefined);
};

/**
 * A 403 WITH a token is an authorization refusal; without one it is a missing
 * credential (the auth-refresh path). The grants request itself is excluded:
 * answering its refusal by reloading the grants would loop with no backoff.
 */
export const isActingClientRefusal = (
  status: number | undefined,
  token: string | undefined,
  url: string | undefined,
  grantsEndpoint: string,
): boolean => status === 403 && !!token && !(url ?? '').includes(grantsEndpoint);

/** Called by the transport on a 403 that carried a token — a refusal, not an expiry. */
export const signalActingClientRefused = (): void => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ACTING_CLIENT_REFUSED_EVENT));
};
